/**
 * @fileoverview The sections of a gardener's public garden page (/@amy, /g/code),
 * top to bottom, as the owner's approved mock has them.
 *
 * - GardenHero: a full-width cover photo with a dark fade (the gardener's pick,
 *   else their newest public photo; a green wash without one), Grow's mark and
 *   a Share button above, and over the photo their profile photo, "Amy's
 *   garden" and "@amy". No town, no address, no notes.
 * - StatChips: "14 plants", "3 beds", "First harvest Oct 6", each only when known;
 *   each is a button that scrolls to its section (plants and the first harvest to
 *   the plant grid, beds to the bed row), as far as the section is on the page.
 * - SocialLinks: where else the gardener can be found (Instagram, YouTube ...).
 * - LatestShare: the words of their newest shared post with its day and plant.
 * - WordsCard: the card the gardener's words sit in (LatestShare's, and the
 *   plant, bed and share pages' own words: itemPageSections).
 * - BedRail: the beds in a row that scrolls inside itself; each card is its
 *   own photo scroller (the bed's picture, then the latest photo of each public
 *   plant in it, each with its day) with the name and plant count under it.
 * - PlantGrid: plant cards with a stage chip; a plant with more than one
 *   public photo scrolls through them (swipe, small dots, the day over each); a
 *   plant with none shows a plain leaf tile. A plant with a public Plant Doctor
 *   checkup has a stethoscope link ("2 checkups") under its name, which opens
 *   the plant's page at the checkups (#checkups); the rest of the card opens it
 *   at the top.
 * - PhotoScroller: the swipeable photo strip the cards share. It scrolls
 *   inside itself, so the page never scrolls sideways. An item page's own strip
 *   (sharp) draws each photo from its large copy on a wide screen (D53);
 *   phones and the cards keep the card copy.
 *
 * A card opens its plant's or bed's own page: its name is the link (so a
 * keyboard or screen reader finds it) stretched over the card, the photo strip
 * above it, and a tap on the strip opens the page too, on the photo that was
 * showing (?photo=N, PHOTO_PARAM).
 *
 * Only what the backend sends is drawn, and it sends only public items
 * (services/gardens/publicGarden). Images are lazy-loaded at fixed shapes, so
 * nothing jumps as they arrive.
 *
 * Where: GardenPage, and the plant, bed and share pages (itemPageSections).
 * API: none (the page's data is gardens getGarden / getGardenBySlug).
 * Settings: SHARE_COLLAGE_TEMPLATES (config/tunables/plantList),
 * GARDEN_LARGE_PHOTO_MIN_WIDTH_PX (config/tunables/garden).
 * Words (en/es): gardenPage.page.* (titleOf, title, latest,
 * latestFrom, shareText, photoOf, noPhoto, cardPhotos, avatarAlt, beds, plants, count.*,
 * checkupCount, checkupCount_one, stage.*), profile.links.website, share.actions.*.
 *
 * @module gardenPageSections
 */

import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Sprout, Stethoscope } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Pill } from '@/shared/components/ui/Pill';
import { PhotoButton } from '@/shared/components/ui/PhotoButton';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { SocialIcon } from '@/shared/components/SocialIcon';
import { SOCIAL_MARKS } from '@/shared/lib/socialLinks';
import {
  gardenItemPathFor,
  gardenUrlFor,
  type GardenBed,
  type GardenPhoto,
  type GardenPlant,
  type PublicGarden,
} from '@/shared/api/gardens';
import { CopyLinkButton, ShareError, ShareLinkButton } from './ShareButtons';
import { canShareLink } from './lib/shareLink';
import { apiSrc, cropLine, photosOfPlant, useShortDay } from './lib/gardenPage';
import { SHARE_COLLAGE_TEMPLATES } from '@/config/tunables/plantList';
import { FramedPhoto } from '@/shared/components/ui/FramedPhoto';
import { GARDEN_LARGE_PHOTO_MIN_WIDTH_PX } from '@/config/tunables/garden';

/** The Share and Copy link buttons' look on the cover photo: a small dark pill. */
/** The garden page's small Share, beside the gardener's name: a quiet outline, not a button over a photo. */
const SHARE_SMALL_CLASS = 'h-8 rounded-full border bg-card px-3 text-sm text-foreground hover:bg-muted';

/** A button laid over a photo (Share in a photo's corner): dark glass, white words, no border. */
export const OVERLAY_BUTTON_CLASS = 'h-10 rounded-full border-0 bg-black/45 px-4 text-sm text-white backdrop-blur hover:bg-black/60 hover:text-white';

/** The page's column: centred, at most 64rem, a phone's gutter either side. */
export const GARDEN_COLUMN_CLASS = 'mx-auto w-full max-w-5xl px-4';

/** The id of the beds' section, which a stat chip scrolls to. */
export const BEDS_SECTION_ID = 'garden-beds';

/** The id of the plants' section, which the plant and first-harvest chips scroll to. */
export const PLANTS_SECTION_ID = 'garden-plants';

/** The id of a plant page's Plant Doctor checkups section: the address fragment (#checkups) a card's chip opens it by. */
export const CHECKUPS_SECTION_ID = 'checkups';

/** The look of a section's heading: small capitals in the page's serif. */
export const SECTION_HEADING_CLASS = 'font-grow text-sm font-semibold uppercase tracking-wider text-muted-foreground';

/**
 * The line under the gardener's name: "@amy" (Grow does not use zones, so none
 * is shown).
 *
 * @param slug - The gardener's address (without its "@"), or nothing.
 * @returns The line, or null without an address.
 */
function handleLine(slug: string | null | undefined): string | null {
  return slug ? `@${slug}` : null;
}

/**
 * The gardener's profile photo as a ring-edged circle, or their initial in
 * its place.
 *
 * @param props.src - The photo's API path, or null.
 * @param props.name - The name shown, for the initial and the photo's alt text.
 */
const Avatar: React.FC<{ src: string | null | undefined; name: string | null }> = ({ src, name }) => {
  const { reactTranslate: t } = useTranslation();
  const initial = (name?.replace(/^@/, '').trim()[0] ?? '').toUpperCase();
  const [broken, setBroken] = React.useState(false);
  if (src && !broken) {
    return (
      <img
        src={apiSrc(src)}
        alt={name ? t('gardenPage.page.avatarAlt', { name }) : ''}
        width={56}
        height={56}
        className="h-14 w-14 shrink-0 rounded-full border bg-card object-cover"
        onError={() => setBroken(true)}
      />
    );
  }
  if (!initial) return null;
  return (
    <span
      aria-hidden="true"
      className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border bg-card font-grow text-2xl font-bold text-primary"
    >
      {initial}
    </span>
  );
};

/**
 * A gardener's name as a title says it: without the "@" of an address
 * ("kitchengardensdaily's garden", not "@kitchengardensdaily's garden").
 *
 * @param name - The name the garden shows, or null.
 * @returns The name for a title, or null.
 */
export const titleName = (name: string | null | undefined): string | null => (name ? name.replace(/^@/, '').trim() || null : null);

/** What the hero is told: the whole garden. */
interface GardenHeroProps {
  /** The garden as the page got it. */
  garden: PublicGarden;
}

/**
 * The top of the page, plain (owner, 2026-10-13, after a design critique:
 * the big cropped cover photo was "disorienting"): the profile photo, "Amy's
 * garden", one line of the season's counts and the gardener's links, and a
 * small Share. No cover photo here: the plant cards are the pictures. The
 * cover still heads the garden's card in the Gardens directory and its link
 * previews; the gardener picks it in Profile > Public pages (the page's own
 * "Change cover" link went with the cover photo: owner, 2026-10-14, "change
 * cover image link is there and there is no cover image").
 *
 * @param props - GardenHeroProps.
 */
export const GardenHero: React.FC<GardenHeroProps> = ({ garden }) => {
  const { reactTranslate: t } = useTranslation();
  const [error, setError] = React.useState<string | null>(null);
  const name = garden.gardener?.name ?? null;
  const shown = titleName(name);
  const title = shown ? t('gardenPage.page.titleOf', { name: shown }) : t('gardenPage.page.title');
  const line = handleLine(garden.slug);
  const link = gardenUrlFor(garden.code, garden.slug);
  return (
    <header className={cn(GARDEN_COLUMN_CLASS, 'pt-6')}>
      <div className="flex items-center gap-3">
        <Avatar src={garden.avatar} name={name} />
        <h1 className="min-w-0 flex-1 font-grow text-2xl font-bold leading-tight [overflow-wrap:anywhere] [text-wrap:balance]">{title}</h1>
      </div>
      {/* Under the name, one line: the counts, the gardener's links, and Share. */}
      <div className="mt-2">
        <div className="min-w-0">
          {/* The address only when the title does not already carry it (a gardener shown by another name). */}
          {line && !String(title).includes(line.replace(/^@/, '')) && <p className="text-sm text-muted-foreground [overflow-wrap:anywhere]">{line}</p>}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-0 text-sm text-muted-foreground">
            <StatChips season={garden.season} />
            <SocialLinks links={garden.links} />
            {canShareLink()
              ? <ShareLinkButton link={link} title={title} text={shown ? t('gardenPage.page.shareText', { name: shown }) : undefined} onError={setError} className={SHARE_SMALL_CLASS} />
              : <CopyLinkButton link={link} onError={setError} className={SHARE_SMALL_CLASS} />}
          </div>
          {error && <div className="mt-1 max-w-xs rounded-md bg-card p-2"><ShareError text={error} /></div>}
        </div>
      </div>
    </header>
  );
};

/**
 * Scrolls a section of the page into view, gently unless the visitor asks for
 * less motion, and moves keyboard focus to it so a screen reader follows.
 *
 * @param id - The section's id; nothing happens when it is not on the page.
 */
function scrollToSection(id: string): void {
  const el = document.getElementById(id);
  if (!el) return;
  const calm = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  el.scrollIntoView({ behavior: calm ? 'auto' : 'smooth', block: 'start' });
  el.focus({ preventScroll: true });
}

/**
 * The season in numbers, as plain words in one line ("5 plants · 1 bed"), no
 * longer pills that looked like buttons: public plants, public beds and the
 * first harvest recorded this year, each only when it is there. Each still
 * scrolls to its section when tapped: plants and the first harvest to the
 * plant grid, beds to the bed row.
 *
 * @param props.season - The garden's season, or nothing (then nothing is drawn).
 */
export const StatChips: React.FC<{ season: PublicGarden['season'] }> = ({ season }) => {
  const { reactTranslate: t } = useTranslation();
  const shortDay = useShortDay();
  if (!season) return null;
  const harvest = shortDay(season.firstHarvest);
  const chips = [
    season.plants > 0 ? { text: t(season.plants === 1 ? 'gardenPage.page.count.plants_one' : 'gardenPage.page.count.plants', { count: season.plants }), target: PLANTS_SECTION_ID } : null,
    season.beds > 0 ? { text: t(season.beds === 1 ? 'gardenPage.page.count.beds_one' : 'gardenPage.page.count.beds', { count: season.beds }), target: BEDS_SECTION_ID } : null,
    harvest ? { text: t('gardenPage.page.count.firstHarvest', { day: harvest }), target: PLANTS_SECTION_ID } : null,
  ].filter((chip): chip is { text: string; target: string } => chip !== null);
  if (!chips.length) return null;
  return (
    <ul className="flex flex-wrap items-center" aria-label={t('gardenPage.page.seasonNumbers')}>
      {chips.map((chip, index) => (
        <li key={chip.text} className="flex items-center">
          {index > 0 && <span aria-hidden="true" className="px-1.5">·</span>}
          <button
            type="button"
            onClick={() => scrollToSection(chip.target)}
            className="inline-flex min-h-9 items-center text-sm text-muted-foreground hover:text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {chip.text}
          </button>
        </li>
      ))}
    </ul>
  );
};

/**
 * Where else the gardener can be found: their marks and names as links that
 * open in a new tab. Only http and https addresses are linked.
 *
 * @param props.links - The garden's links.
 */
export const SocialLinks: React.FC<{ links: PublicGarden['links'] }> = ({ links }) => {
  const { reactTranslate: t } = useTranslation();
  const shown = (links ?? []).filter((link) => /^https?:\/\//i.test(link.url));
  if (!shown.length) return null;
  return (
    <ul className="flex flex-wrap items-center gap-x-3 gap-y-0 text-sm">
      {shown.map((link) => (
        <li key={link.key}>
          <a
            href={link.url}
            target="_blank"
            rel="noopener noreferrer nofollow"
            className="inline-flex min-h-9 items-center gap-1.5 font-medium text-primary underline-offset-4 hover:underline"
          >
            <SocialIcon name={link.key} size={16} />
            {link.key === 'website' ? t('profile.links.website') : SOCIAL_MARKS[link.key].title}
          </a>
        </li>
      ))}
    </ul>
  );
};

/** What a words card is told: the words, and the line under them. */
interface WordsCardProps {
  /** The gardener's words, as shown. */
  words: string;
  /** The line under the words (the day, what it was of, who shared it), or none. */
  meta?: string | null;
}

/**
 * The gardener's words in a rounded card, in the page's serif, with a quiet
 * line under them. Never private notes: callers pass only words that went
 * out on a share link that still stands.
 *
 * @param props - WordsCardProps.
 */
export const WordsCard: React.FC<WordsCardProps> = ({ words, meta }) => (
  <figure className="rounded-2xl border bg-card p-4">
    <blockquote className="font-grow text-lg leading-snug [overflow-wrap:anywhere]">{`“${words}”`}</blockquote>
    {meta && <figcaption className="mt-2 text-sm text-muted-foreground">{meta}</figcaption>}
  </figure>
);

/** What the latest share is told: the share, and who shared it. */
interface LatestShareProps {
  /** The garden's latest share. */
  latest: NonNullable<PublicGarden['latest']>;
  /** The gardener's name as shown, for "words from a post Amy shared". */
  name: string | null;
}

/**
 * "Latest share": the words from the gardener's newest shared post in a card,
 * with its day and what it was of. Never private notes: the backend sends
 * only words that went out on a share link that still stands.
 *
 * @param props - LatestShareProps.
 */
export const LatestShare: React.FC<LatestShareProps> = ({ latest, name }) => {
  const { reactTranslate: t } = useTranslation();
  const shortDay = useShortDay();
  const meta = [
    shortDay(latest.date),
    latest.plant ?? latest.bed,
    name ? t('gardenPage.page.latestFrom', { name }) : null,
  ].filter(Boolean).join(' · ');
  return (
    <section>
      <h2 className={SECTION_HEADING_CLASS}>{t('gardenPage.page.latest')}</h2>
      <div className="mt-3"><WordsCard words={latest.words} meta={meta} /></div>
    </section>
  );
};

/** What a photo scroller is told. */
interface PhotoScrollerProps {
  /** The photos, in order; none draws the fallback. */
  photos: GardenPhoto[];
  /** What the strip is, for a screen reader ("Photos of Romaine lettuce"). */
  label: string;
  /** The strip's shape, as a Tailwind aspect class. */
  aspect: string;
  /** A tap on a photo, told which one (from 0); the card opens its page on that photo. */
  onOpen?: (index: number) => void;
  /** The photo to show (from 0): at once when the strip first draws, smoothly when it changes later. */
  show?: number;
  /** Drawn when there is no photo. */
  fallback: React.ReactNode;
  /** Laid over the strip and not scrolling with it (a stage chip); must not catch taps. */
  children?: React.ReactNode;
  /**
   * With three photos or more, a collage of them first (a bed's), as a share's
   * collage is laid out (SHARE_COLLAGE_TEMPLATES): a tile tapped goes to its
   * photo, or with onOpen opens the page on it. Slides count from the collage.
   */
  collage?: boolean;
  /**
   * The garden page's cards (owner, 2026-10-13: one label per photo): no day,
   * seed-packet or plant label over the photos; the card says them under its
   * name instead. An item page's own large strip keeps them.
   */
  quiet?: boolean;
  /**
   * An item page's own strip, drawn big: on a screen at least
   * GARDEN_LARGE_PHOTO_MIN_WIDTH_PX wide each photo is drawn from its large
   * copy (photo.large) where the backend sends one (D53). Phones and the
   * garden page's cards keep the card copy.
   */
  sharp?: boolean;
}

/**
 * One photo of a strip: the card copy, or on a wide screen its large copy
 * when it has one and the strip is drawn big (a <picture> whose source the
 * browser picks by the screen's width, so a phone never fetches the large
 * one).
 *
 * @param props.photo - The photo.
 * @param props.sharp - Whether the strip is an item page's own (PhotoScrollerProps.sharp).
 */
const StripImage: React.FC<{ photo: GardenPhoto; sharp: boolean }> = ({ photo, sharp }) => {
  const img = <FramedPhoto src={apiSrc(photo.src)} alt="" loading="lazy" draggable={false} />;
  if (!sharp || !photo.large) return img;
  return (
    <picture>
      <source media={`(min-width: ${GARDEN_LARGE_PHOTO_MIN_WIDTH_PX}px)`} srcSet={apiSrc(photo.large)} />
      {img}
    </picture>
  );
};

/**
 * A strip of photos that scrolls sideways inside itself (swipe, trackpad,
 * arrow keys, or the previous and next buttons on its sides, which work even
 * where a phone gives the swipe to the page), snapping one photo at a time, with small dots under a card that
 * has more than one (each a button, so a mouse can use them) and the day the
 * photo was taken over each photo. The page itself never scrolls sideways.
 *
 * @param props - PhotoScrollerProps.
 */
export const PhotoScroller: React.FC<PhotoScrollerProps> = ({ photos, label, aspect, onOpen, show, fallback, children, collage = false, quiet = false, sharp = false }) => {
  const { reactTranslate: t } = useTranslation();
  const shortDay = useShortDay();
  const strip = React.useRef<HTMLDivElement>(null);
  const [index, setIndex] = React.useState(0);
  /** A collage leads the photos (three or more, when asked for). */
  const withCollage = collage && photos.length >= 3;
  /** The slides: the collage, if any, then each photo. */
  const slideCount = photos.length + (withCollage ? 1 : 0);
  const many = slideCount > 1;
  /** The collage's tiles, as fractions of its slide, the first photo in the largest. */
  const tiles = withCollage ? [...(SHARE_COLLAGE_TEMPLATES[Math.min(9, photos.length)] ?? [])]
    .map((tile) => tile)
    .sort((a, b) => b[2] * b[3] - a[2] * a[3]) : [];

  /** Notes which photo is showing as the strip scrolls. */
  const noteScroll = (): void => {
    const el = strip.current;
    if (el && el.clientWidth > 0) setIndex(Math.min(slideCount - 1, Math.round(el.scrollLeft / el.clientWidth)));
  };

  /**
   * Scrolls to one photo, for a dot.
   *
   * @param i - The photo's place.
   */
  const goTo = (i: number): void => {
    const el = strip.current;
    if (el) el.scrollTo({ left: i * el.clientWidth, behavior: 'smooth' });
  };

  /** Whether the strip has drawn once: the first photo to show is jumped to, later ones are slid to. */
  const drawn = React.useRef(false);
  React.useEffect(() => {
    const el = strip.current;
    if (!el || show === undefined || show < 0 || show >= slideCount) { drawn.current = true; return; }
    el.scrollTo({ left: show * el.clientWidth, behavior: drawn.current ? 'smooth' : 'auto' });
    setIndex(show);
    drawn.current = true;
  }, [show, slideCount]);

  return (
    <div className={cn('relative z-10 w-full overflow-hidden', photos.length === 0 && 'bg-muted', aspect)}>
      {photos.length === 0 ? fallback : (
        <div
          ref={strip}
          role="group"
          aria-roledescription={many ? 'carousel' : undefined}
          aria-label={label}
          tabIndex={many ? 0 : undefined}
          onScroll={many ? noteScroll : undefined}
          onClick={onOpen ? () => onOpen(index) : undefined}
          className={cn(
            'flex h-full w-full snap-x snap-mandatory overflow-x-auto overscroll-x-contain [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
            onOpen && 'cursor-pointer',
          )}
        >
          {withCollage && (
            <div className="relative h-full w-full shrink-0 snap-center bg-[#f4efe4] p-[3px]">
              <div className="relative h-full w-full">
                {photos.slice(0, 9).map((photo, i) => {
                  const tile = tiles[i];
                  if (!tile) return null;
                  const [x, y, w, h] = tile;
                  return (
                    <button
                      key={`collage-${photo.src}-${i}`}
                      type="button"
                      aria-label={t('gardenPage.page.photoOf', { n: i + 1, total: photos.length })}
                      onClick={(event) => { event.stopPropagation(); if (onOpen) onOpen(i + 1); else goTo(i + 1); }}
                      className="absolute p-[2px]"
                      style={{ left: `${x * 100}%`, top: `${y * 100}%`, width: `${w * 100}%`, height: `${h * 100}%` }}
                    >
                      <span className="relative block h-full w-full overflow-hidden rounded-[2px] bg-muted data-[small=true]:bg-transparent">
                        <FramedPhoto src={apiSrc(photo.src)} alt="" loading="lazy" draggable={false} />
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {photos.map((photo, i) => {
            const day = shortDay(photo.date);
            return (
              <div key={`${photo.src}-${i}`} className="relative h-full w-full shrink-0 snap-center bg-muted data-[small=true]:bg-transparent">
                <StripImage photo={photo} sharp={sharp} />
                {photo.plant && !quiet && (
                  <Pill size="sm" tone="photo" className="absolute left-2 top-2 max-w-[calc(100%-1rem)]">
                    {[photo.plant, photo.stage ? photo.stage.name : null].filter(Boolean).join(' · ')}
                  </Pill>
                )}
                {quiet ? null : photo.packet ? (
                  <Pill size="sm" tone="photo" className="absolute bottom-2 left-2">{t('gardenPage.page.seedPacket')}</Pill>
                ) : day && (
                  <Pill size="sm" tone="photo" className="absolute bottom-2 left-2">{day}</Pill>
                )}
              </div>
            );
          })}
        </div>
      )}
      {children}
      {many && index > 0 && (
        <PhotoButton size="md" label={t('gardenPage.page.prevPhoto')} onClick={() => goTo(index - 1)} className="absolute left-1.5 top-1/2 -translate-y-1/2">
          <ChevronLeft className="h-5 w-5" aria-hidden="true" />
        </PhotoButton>
      )}
      {many && index < slideCount - 1 && (
        <PhotoButton size="md" label={t('gardenPage.page.nextPhoto')} onClick={() => goTo(index + 1)} className="absolute right-1.5 top-1/2 -translate-y-1/2">
          <ChevronRight className="h-5 w-5" aria-hidden="true" />
        </PhotoButton>
      )}
      {many && (
        <div className="absolute bottom-1 right-1 flex items-center">
          {Array.from({ length: slideCount }, (_, i) => (
            <button
              key={i}
              type="button"
              aria-label={t('gardenPage.page.photoOf', { n: i + 1, total: slideCount })}
              aria-current={i === index}
              onClick={(event) => { event.stopPropagation(); goTo(i); }}
              className="flex h-5 w-4 items-center justify-center"
            >
              <span className={cn('block h-1.5 w-1.5 rounded-full bg-white shadow', i === index ? 'opacity-100' : 'opacity-50')} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * A card's page address, opened on the photo the visitor was looking at: an
 * item page (/@amy/…) takes ?photo=N (from 1); the first photo, and a share
 * page (/s/…), need nothing added.
 *
 * @param to - The card's page.
 * @param shown - The photo showing on the card, from 0.
 * @returns The address to open.
 */
function onPhoto(to: string, shown: number): string {
  return shown > 0 && to.startsWith('/@') ? `${to}?${PHOTO_PARAM}=${shown + 1}` : to;
}

/** The address's query key for the photo an item page opens on (?photo=2), from 1. */
export const PHOTO_PARAM = 'photo';

/** The quiet tile a card shows without a photo: a leaf on the muted ground. */
const LeafTile: React.FC = () => {
  const { reactTranslate: t } = useTranslation();
  return (
    <span className="flex h-full w-full items-center justify-center bg-muted" role="img" aria-label={t('gardenPage.page.noPhoto')}>
      <Sprout className="h-10 w-10 text-primary/35" aria-hidden="true" />
    </span>
  );
};

/** The scrollbar-free, snapping sideways row the beds sit in; it reaches the screen's edges on a phone. */
const RAIL_CLASS = '-mx-4 mt-3 flex snap-x gap-3 overflow-x-auto px-4 pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden';

/**
 * A card's name as the link that opens its page, stretched over the card (the
 * photo strip sits above it, and opens the page on a tap itself).
 *
 * @param props.to - The page, or nothing (then the name is plain text).
 * @param props.className - The name's look.
 * @param props.children - The name.
 */
const CardName: React.FC<{ to: string | null; className?: string; children: React.ReactNode }> = ({ to, className, children }) => (
  to
    ? <Link to={to} className={cn(className, 'after:absolute after:inset-0 after:content-[""] focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-ring focus-visible:after:rounded-2xl')}>{children}</Link>
    : <span className={className}>{children}</span>
);

/** What the bed rail is told. */
interface BedRailProps {
  /** The public beds. */
  beds: GardenBed[];
  /** The garden's address, for each bed's own page; without it a card opens its share page, or nothing. */
  gardenSlug: string | null;
}

/**
 * The beds as a row of cards that scrolls inside itself. Each card is a photo
 * scroller (its own picture, then the latest photo of each public plant in
 * it) with the bed's name and its plant count under it.
 *
 * @param props - BedRailProps.
 */
export const BedRail: React.FC<BedRailProps> = ({ beds, gardenSlug }) => {
  const { reactTranslate: t } = useTranslation();
  const navigate = useNavigate();
  return (
    <section id={BEDS_SECTION_ID} tabIndex={-1} className="scroll-mt-4 focus:outline-none">
      <h2 className={SECTION_HEADING_CLASS}>{t('gardenPage.page.beds')}</h2>
      <ul className={RAIL_CLASS}>
        {beds.map((bed, index) => {
          const to = gardenSlug && bed.slug ? gardenItemPathFor(gardenSlug, bed.slug) : bed.code ? `/s/${bed.code}` : null;
          // Its own photos only: Grow's seasonal painting is no bed's photo (the backend leaves it out), so a bed with none shows the leaf.
          const photos = bed.photos ?? [];
          const count = typeof bed.plants === 'number' && bed.plants > 0
            ? t(bed.plants === 1 ? 'gardenPage.page.count.plants_one' : 'gardenPage.page.count.plants', { count: bed.plants })
            : null;
          const line = [count, cropLine(bed.crops, t)].filter(Boolean).join(' · ');
          return (
            <li key={`${bed.slug ?? bed.name}-${index}`} className="relative w-[220px] shrink-0 snap-start overflow-hidden rounded-2xl border bg-card sm:w-[260px]">
              <PhotoScroller
                photos={photos}
                label={t('gardenPage.page.cardPhotos', { name: bed.name })}
                aspect="aspect-[4/3]"
                collage
                quiet
                onOpen={to ? (shown) => navigate(onPhoto(to, shown)) : undefined}
                fallback={<LeafTile />}
              />
              <div className="px-4 py-3">
                <CardName to={to} className="block truncate font-grow text-base font-bold">{bed.name}</CardName>
                {line && <p className="truncate text-sm text-muted-foreground">{line}</p>}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

/** What the plant grid is told. */
interface PlantGridProps {
  /** The public plants, in the backend's order. */
  plants: GardenPlant[];
  /** The garden's address, for each plant's own page; without it a card opens its share page, or nothing. */
  gardenSlug: string | null;
  /** The section's heading; the garden page's "Plants" when not given. */
  heading?: string;
  /** The section's id, for a stat chip to scroll to; none when not given. */
  id?: string;
}

/**
 * The plants as a grid of cards: a photo strip with the stage as a chip over
 * its corner and the plant's one name under it (the backend's names,
 * services/grow/plantNaming). Two columns on a phone, three from a tablet up.
 *
 * @param props - PlantGridProps.
 */
export const PlantGrid: React.FC<PlantGridProps> = ({ plants, gardenSlug, heading, id }) => {
  const { reactTranslate: t } = useTranslation();
  const shortDay = useShortDay();
  const navigate = useNavigate();
  return (
    <section id={id} tabIndex={id ? -1 : undefined} className={id ? 'scroll-mt-4 focus:outline-none' : undefined}>
      <h2 className={SECTION_HEADING_CLASS}>{heading ?? t('gardenPage.page.plants')}</h2>
      {/* Four columns from a tablet, five on a wide screen: the photos are stored at card size and blur when drawn larger. */}
      <ul className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4 xl:grid-cols-5">
        {plants.map((plant, index) => {
          const checkups = plant.checkupCount ?? 0;
          const page = gardenSlug && plant.slug ? gardenItemPathFor(gardenSlug, plant.slug) : null;
          const to = page ?? (plant.code ? `/s/${plant.code}` : null);
          const photos = photosOfPlant(plant);
          // The latest photo's day, or that the picture is its seed packet, said under the name (not over the photo).
          const first = photos[0];
          const photoLine = first?.packet ? t('gardenPage.page.seedPacket') : shortDay(first?.date) ? t('gardenPage.page.photoDay', { day: shortDay(first?.date) ?? '' }) : null;
          return (
            <li key={`${plant.slug ?? plant.variety}-${index}`} className="relative min-w-0 overflow-hidden rounded-2xl border bg-card">
              <PhotoScroller
                photos={photos}
                quiet
                label={t('gardenPage.page.cardPhotos', { name: plant.names.full ?? plant.variety })}
                aspect="aspect-square"
                onOpen={to ? (shown) => navigate(onPhoto(to, shown)) : undefined}
                fallback={<LeafTile />}
              >
                {plant.stage && (
                  <Pill size="sm" tone="photoLight" className="pointer-events-none absolute left-2 top-2">
                    {plant.stage.name}
                  </Pill>
                )}
              </PhotoScroller>
              <div className="px-3 py-3">
                {/* The plant's one name (the backend's names, services/grow/plantNaming); its kind is not said under it. */}
                <CardName to={to} className="block truncate font-grow text-base font-bold">{plant.names.full ?? plant.variety}</CardName>
                {photoLine && <p className="truncate text-xs text-muted-foreground">{photoLine}</p>}
                {/* Over the card's stretched name link, so it opens the plant's page at its checkups. */}
                {checkups > 0 && page && (
                  <Link
                    to={`${page}#${CHECKUPS_SECTION_ID}`}
                    className="relative z-10 mt-1 inline-flex min-h-[32px] items-center gap-1 text-sm font-medium text-primary underline-offset-4 hover:underline"
                  >
                    <Stethoscope className="h-4 w-4" aria-hidden="true" />
                    {t(checkups === 1 ? 'gardenPage.page.checkupCount_one' : 'gardenPage.page.checkupCount', { count: checkups })}
                  </Link>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
};
